<template>
  <div class="payment-page">

    <div class="payment-grid">

      <!-- LEFT: Order Summary -->
      <div class="order-card">

        <div class="order-header">
          <font-awesome-icon icon="shield-alt" class="order-header-icon" />
          <div>
            <div class="order-header-title">Secure Checkout</div>
            <div class="order-header-sub">Powered by industry-standard encryption</div>
          </div>
        </div>

        <div class="order-divider"></div>

        <!-- Plan name + price -->
        <div class="plan-row">
          <div>
            <div class="plan-name">Mapiibs — {{ plan?.plan?.name ?? '' }}</div>
            <div class="plan-cycle">{{ billingCycleLabel }}</div>
          </div>
          <div class="plan-price">
            {{ currentUserCurrency?.symbol }}{{ $filters.formatMoney(plan.price) }}
          </div>
        </div>

        <!-- Order total -->
        <div class="order-total-block">
          <div class="order-total-row">
            <span class="order-total-label">Order Total</span>
            <span class="order-total-amount">
              {{ currentUserCurrency?.symbol }}{{ $filters.formatMoney(plan.price) }}
            </span>
          </div>
          <div class="order-total-sub">
            Billed {{ billingCycleLabel }} · {{ plan?.max_employee }} users
          </div>
        </div>

        <!-- Charge notice -->
        <div class="charge-notice">
          <font-awesome-icon icon="info-circle" class="notice-icon" />
          <span>
            You'll be charged
            <strong>{{ currentUserCurrency?.symbol }}{{ $filters.formatMoney(plan.price) }} {{ billingCycleLabel }}</strong>
            plus applicable taxes until you cancel your licence.
          </span>
        </div>

      </div>

      <!-- RIGHT: Payment Methods -->
      <div class="payment-card">

        <div class="payment-title">Select Payment Method</div>
        <div class="payment-sub">Choose your preferred payment provider to complete your purchase.</div>

        <!-- Loading channels -->
        <div v-if="loadingChannels" class="channels-loading">
          <div class="spinner-border text-primary" style="width:2rem;height:2rem;" role="status"></div>
          <span class="ms-2 text-muted-cell">Loading payment options...</span>
        </div>

        <!-- No channels -->
        <div v-else-if="payment_channels.length === 0" class="state-block">
          <div class="state-icon"><font-awesome-icon icon="credit-card" /></div>
          <p class="state-title">No payment methods available</p>
          <p class="state-sub">Please contact support to complete your purchase.</p>
        </div>

        <!-- Channel list -->
        <div v-else class="channel-list">
          <div
            v-for="ch in payment_channels"
            :key="ch.id"
            class="channel-item"
            :class="{ 'channel-item--processing': processing === ch.id }"
          >

            <!-- Flutterwave -->
            <template v-if="ch.name.toLowerCase() === 'flutterwave'">
              <button class="channel-btn" @click="buyNow(ch)" :disabled="!!processing">
                <div class="channel-logo-wrap">
                  <img :src="hostName + 'images/payments/' + ch.logo" :alt="ch.name" class="channel-logo" />
                </div>
                <div class="channel-info">
                  <div class="channel-name">{{ ch.name }}</div>
                  <div class="channel-desc">Pay with card, bank transfer or USSD</div>
                </div>
                <div class="channel-arrow">
                  <font-awesome-icon v-if="processing === ch.id" icon="spinner" spin />
                  <font-awesome-icon v-else icon="chevron-right" />
                </div>
              </button>
            </template>

            <!-- Paystack -->
            <template v-if="ch.name.toLowerCase() === 'paystack'">
              <div class="channel-btn paystack-wrap" @click="currentData(plan, $route.params.id, $route.query.query)">
                <div class="channel-logo-wrap">
                  <img :src="hostName + 'images/payments/' + ch.logo" :alt="ch.name" class="channel-logo" />
                </div>
                <div class="channel-info">
                  <div class="channel-name">{{ ch.name }}</div>
                  <div class="channel-desc">Pay with card or bank account</div>
                </div>
                <paystack
                  buttonClass="paystack-hidden-btn"
                  buttonText=""
                  :publicKey="ch.public_key"
                  :email="currentUser.email"
                  :amount="plan.amount * 100"
                  :reference="'PST-' + reference"
                  :onSuccess="onSuccessfulPayment"
                  :onCanel="onCancelledPayment"
                />
                <div class="channel-arrow">
                  <font-awesome-icon icon="chevron-right" />
                </div>
              </div>
            </template>

          </div>
        </div>

        <div class="order-divider"></div>

        <!-- Cancel -->
        <div class="cancel-wrap">
          <router-link :to="$url('/subscription-plan')" class="btn-cancel">
            <font-awesome-icon icon="times" /> Cancel Payment
          </router-link>
        </div>

        <!-- Security note -->
        <div class="security-note">
          <font-awesome-icon icon="lock" class="me-1" />
          Your payment is secured with 256-bit SSL encryption
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { reusableMixin } from '../../../lib/reusableMixin';
import host from '../../../main';
import { useFlutterwave } from 'flutterwave-vue3';
import { useToast } from 'vue-toastification';
import paystack from 'vue3-paystack';
import { nanoid } from 'nanoid';

export default {
  name: 'SubscriptionPayment',

  components: { paystack },

  mixins: [reusableMixin],

  data() {
    return {
      toast:            useToast(),
      plan:             {},
      payment_channels: [],
      loadingChannels:  false,
      processing:       null,   // stores ch.id of whichever channel is mid-payment

      payment_data: { plan_details_id: null },
      currentId:    null,
      query:        null,
    };
  },

  computed: {
    hostName() { return host.hostName; },

    reference() { return nanoid(15); },

    billingCycleLabel() {
      const map = { Monthly: 'per month', Quarterly: 'per 3 months', 'Half Year': 'per 6 months', Yearly: 'per year' };
      return map[this.plan?.type] ?? '';
    },
  },

  methods: {
    // ── Helpers ─────────────────────────────────────────────────────────
    buildPaymentData(source, plan, current_id, query, reference, payment_type) {
      return {
        plan_details_id:  current_id,
        plan:             plan?.plan?.id,
        amount:           source?.amount ?? plan?.amount,
        type:             plan?.type,
        customer:         source?.customer ?? this.currentUser.fullname,
        query,
        reference_number: source?.flw_ref ?? source?.trxref ?? reference,
        meta:             source,
        payment_type,
      };
    },

    // ── Paystack ─────────────────────────────────────────────────────────
    currentData(plan, currentId, query) {
      this.currentId = currentId;
      this.plan      = plan;
      this.query     = query;
      this.payment_data.plan_details_id = currentId;

      axios.post('/subscription/check-subscription', this.payment_data)
        .catch(err => {
          this.toast.error(err.response?.data?.message ?? 'Subscription check failed.');
          this.$router.push('/subscribe');
        });
    },

    onSuccessfulPayment(response) {
      const data = this.buildPaymentData(response, this.plan, this.currentId, this.query, null, 'Paystack');
      axios.post('/subscription/buy', data)
        .then(res => {
          this.toast.success(res.data.message);
          setTimeout(() => this.$router.push('/subscription-plan'), 600);
        })
        .catch(err => this.toast.error(err.response?.data?.message ?? 'Payment failed.'));
    },

    onCancelledPayment() {},

    // ── Flutterwave ───────────────────────────────────────────────────────
    buyNow(ch) {
      this.processing = ch.id;
      this.payment_data.plan_details_id = this.$route.params.id;

      axios.post('/subscription/check-subscription', this.payment_data)
        .then(res => {
          if (res.status === 200) this.makePayment(ch);
        })
        .catch(err => {
          this.toast.error(err.response?.data?.message ?? 'Subscription check failed.');
          this.processing = null;
        });
    },

    makePayment(ch) {
      const { plan, currentUserCurrency, currentUser, $route, buildPaymentData } = this;
      const toast    = this.toast;
      const router   = this.$router;
      const query    = $route.query.query;
      const current_id = $route.params.id;
      const self     = this;

      useFlutterwave({
        amount:         plan.amount,
        country:        'NG',
        currency:       currentUserCurrency?.code,
        customer:       { email: currentUser.email, name: currentUser.fullname, phone_number: currentUser.phone_no },
        customizations: { description: '', logo: '', title: 'Mapiibs' },
        meta:           { user_id: currentUser.id, token: 'FLW-' + self.reference },
        payment_options: 'card',
        public_key:     ch.public_key,
        tx_ref:         'tx_ref_' + nanoid(20),

        callback(data) {
          const payload = buildPaymentData.call(self, data, plan, current_id, query, null, 'Flutterwave');
          axios.post('/subscription/buy', payload)
            .then(res => {
              toast.success(res.data.message);
              setTimeout(() => router.push('/subscription-plan'), 1000);
            })
            .catch(err => toast.error(err.response?.data?.message ?? 'Payment failed.'));
        },

        onclose() {
          self.processing = null;
        },
      });
    },

    // ── Data fetching ────────────────────────────────────────────────────
    getPlanDetail() {
      axios.get('/subscription/detail/' + this.$route.params.id)
        .then(res => { this.plan = res.data.data; })
        .catch(() => {});
    },

    getPaymentChannels() {
      this.loadingChannels = true;
      axios.get('/subscription/payment-channels')
        .then(res => { this.payment_channels = res.data.data; })
        .catch(() => {})
        .finally(() => { this.loadingChannels = false; });
    },
  },

  mounted() {
    this.getPlanDetail();
    this.getPaymentChannels();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.payment-page { display:flex; justify-content:center; padding:24px 16px; }

/* ── Grid ────────────────────────────────────────────────────────────── */
.payment-grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; width:100%; max-width:860px; align-items:start; }

/* ── Shared card ─────────────────────────────────────────────────────── */
.order-card,
.payment-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:32px; }

/* ── Order card ──────────────────────────────────────────────────────── */
.order-header       { display:flex; align-items:center; gap:14px; margin-bottom:20px; }
.order-header-icon  { font-size:22px; color:#16a34a; flex-shrink:0; }
.order-header-title { font-size:15px; font-weight:700; color:#1a2b45; }
.order-header-sub   { font-size:12px; color:#7a90b0; margin-top:2px; }
.order-divider      { border:none; border-top:1px solid #f0f4f8; margin:20px 0; }

.plan-row { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; margin-bottom:20px; }
.plan-name  { font-size:15px; font-weight:700; color:#1a2b45; margin-bottom:4px; }
.plan-cycle { font-size:12px; color:#7a90b0; text-transform:capitalize; }
.plan-price { font-size:20px; font-weight:700; color:#1a2b45; white-space:nowrap; }

.order-total-block { background:#edf4ff; border-radius:10px; padding:16px 18px; margin-bottom:20px; }
.order-total-row   { display:flex; justify-content:space-between; align-items:center; margin-bottom:5px; }
.order-total-label  { font-size:14px; font-weight:700; color:#1a2b45; }
.order-total-amount { font-size:20px; font-weight:800; color:#004AAD; }
.order-total-sub    { font-size:12px; color:#7a90b0; }

.charge-notice { display:flex; align-items:flex-start; gap:10px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:9px; padding:14px; font-size:13px; color:#4a5e78; line-height:1.5; }
.notice-icon   { color:#7a90b0; font-size:14px; flex-shrink:0; margin-top:1px; }

/* ── Payment card ────────────────────────────────────────────────────── */
.payment-title { font-size:17px; font-weight:700; color:#1a2b45; margin-bottom:6px; }
.payment-sub   { font-size:13px; color:#7a90b0; margin-bottom:22px; }
.text-muted-cell { color:#4a5e78; }
.ms-2 { margin-left:8px; }
.me-1 { margin-right:4px; }

.channels-loading { display:flex; align-items:center; padding:24px 0; }

/* ── State ───────────────────────────────────────────────────────────── */
.state-block  { display:flex; flex-direction:column; align-items:center; padding:32px 0; text-align:center; }
.state-icon   { width:52px; height:52px; border-radius:14px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:20px; margin-bottom:12px; }
.state-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 5px; }
.state-sub    { font-size:13px; color:#7a90b0; margin:0; }

/* ── Channel list ────────────────────────────────────────────────────── */
.channel-list { display:flex; flex-direction:column; gap:10px; margin-bottom:8px; }

.channel-item            { border-radius:10px; border:1px solid #e4eaf2; overflow:hidden; transition:border-color .15s; }
.channel-item:hover      { border-color:#004AAD; }
.channel-item--processing { border-color:#004AAD; background:#f4f7fb; }

.channel-btn { width:100%; display:flex; align-items:center; gap:14px; padding:16px 18px; background:transparent; border:none; cursor:pointer; text-align:left; transition:background .15s; }
.channel-btn:hover:not(:disabled) { background:#f8fafc; }
.channel-btn:disabled { opacity:.6; cursor:not-allowed; }

.paystack-wrap { position:relative; cursor:pointer; }
.paystack-wrap:hover { background:#f8fafc; }

/* The hidden paystack button overlays the whole item */
:deep(.paystack-hidden-btn) {
  position:absolute;
  inset:0;
  width:100%;
  height:100%;
  opacity:0;
  cursor:pointer;
  border:none;
  background:none;
}

.channel-logo-wrap { width:48px; height:32px; display:flex; align-items:center; justify-content:center; background:#f8fafc; border-radius:7px; border:1px solid #e4eaf2; flex-shrink:0; padding:4px; }
.channel-logo      { max-width:100%; max-height:100%; object-fit:contain; }

.channel-info { flex:1; min-width:0; }
.channel-name { font-size:13px; font-weight:700; color:#1a2b45; margin-bottom:2px; }
.channel-desc { font-size:12px; color:#7a90b0; }

.channel-arrow { color:#7a90b0; font-size:13px; flex-shrink:0; }

/* ── Cancel + security ───────────────────────────────────────────────── */
.cancel-wrap   { display:flex; justify-content:center; margin-bottom:16px; }
.btn-cancel    { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#7a90b0; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; text-decoration:none; transition:all .15s; }
.btn-cancel:hover { background:#e4eaf2; color:#1a2b45; text-decoration:none; }

.security-note { display:flex; align-items:center; justify-content:center; font-size:12px; color:#b0bdd0; gap:6px; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .payment-grid { grid-template-columns:1fr; }
  .payment-page { padding:16px; }
}
</style>